import * as Popover from '@radix-ui/react-popover'
import { type Editor } from '@tiptap/react'
import DragHandle from '@tiptap-pro/extension-drag-handle-react'
import {
	AlignHorizontalJustifyCenter,
	AlignHorizontalJustifyEnd,
	AlignHorizontalJustifyStart,
	Grip,
	Settings,
	Trash,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '#app/components/ui/button.js'
import { Tabs, TabsList, TabsTrigger } from '#app/components/ui/tabs.js'
import CheckboxesSettings from '../../extensions/input-fields/checkboxes/settings'
import CryptoWalletSettings from '../../extensions/input-fields/crypto-wallet/settings'
import DropdownSettings from '../../extensions/input-fields/dropdown/settings'
import EmailInputSettings from '../../extensions/input-fields/email-input/settings'
import LinkInputSettings from '../../extensions/input-fields/link/settings'
import LongAnswerSettings from '../../extensions/input-fields/long-answer/settings'
import NumberInputSettings from '../../extensions/input-fields/number/settings'
import { ShortAnswerSettings } from '../../extensions/input-fields/short-answer/settings'
import { SubmitButtonSettings } from '../../extensions/input-fields/submit-button/settings'
import { SimpleFormSettings } from '../../extensions/simpleform/settings'
import useContentItemActions from './hooks/useContentItemActions'
import { useData } from './hooks/useData'

export type ContentItemMenuProps = {
	editor: Editor
}

export interface NodeStyle {
	paddingBottom: number
	paddingTop: number
	paddingRight: number
	paddingLeft: number
	background: string
	radiusTopLeft: number
	radiusTopRight: number
	radiusBottomRight: number
	radiusBottomLeft: number
	align: 'flex-start' | 'center' | 'flex-end' | 'baseline' | 'end' | 'normal'
	direction: 'row' | 'column'
	distribute:
		| 'center'
		| 'flex-start'
		| 'flex-end'
		| 'space-around'
		| 'space-between'
	wrap: 'yes' | 'no'
	gap: number
	top: string
	bottom: string
	left: string
	right: string
	positionType: 'sticky' | 'relative' | 'absolute' | 'fixed'
	widthType: 'fill' | 'fixed' | 'fit' | 'relative'
	heightType: 'fill' | 'fixed' | 'fit' | 'relative'
	widthValue?: string
	heightValue?: string
	border: string
	shadow: string
}

export const ContentItemMenu = ({ editor }: ContentItemMenuProps) => {
	const [menuOpen, setMenuOpen] = useState(false)
	const data = useData()

	const actions = useContentItemActions(
		editor,
		data.currentNode,
		data.currentNodePos,
	)

	useEffect(() => {
		if (menuOpen) {
			editor.commands.setMeta('lockDragHandle', true)
		} else {
			editor.commands.setMeta('lockDragHandle', false)
		}
	}, [editor, menuOpen])

	const isAlignable =
		data.currentNode?.content.firstChild?.type.name === 'imageBlock' ||
		data.currentNode?.content.firstChild?.type.name === 'linkButton'

	const hasNoSettings =
		data.currentNode?.content.firstChild?.type.name === 'paragraph' ||
		data.currentNode?.content.firstChild?.type.name === 'heading' ||
		data.currentNode?.content.firstChild?.type.name === 'divider'

	return (
		<>
			{editor.isEditable && (
				<DragHandle
					pluginKey="ContentItemMenu"
					editor={editor}
					onNodeChange={data.handleNodeChange}
					tippyOptions={{
						offset: [0, 16],
						zIndex: 99,
					}}
				>
					<div className="mt-[-4px] flex items-center gap-0.5">
						<div className="flex items-center justify-center rounded-lg border border-border bg-primary-foreground p-1 shadow-sm transition-all duration-300 ease-in-out">
							<Button
								variant="ghost"
								size="s"
								onClick={actions.deleteNode}
								className="w-8"
							>
								<Trash width={16} className="shrink-0" />
							</Button>
							<Popover.Root open={menuOpen} onOpenChange={setMenuOpen}>
								<Popover.Trigger asChild>
									<Button
										variant="ghost"
										size="s"
										className="w-8"
										disabled={hasNoSettings}
									>
										<Settings width={16} className="shrink-0" />
									</Button>
								</Popover.Trigger>
								<Popover.Content
									sideOffset={12}
									alignOffset={-46}
									align="end"
									className="custom-popover z-50 flex w-72 flex-col gap-2 rounded-lg border border-border bg-primary-foreground p-3 shadow-sm"
								>
									{isAlignable && (
										<div className="flex flex-col gap-2">
											<Tabs defaultValue="left" className="w-full">
												<TabsList>
													<TabsTrigger
														value="left"
														className="flex items-center gap-2"
														onClick={() =>
															editor
																.chain()
																.setNodeSelection(data.currentNodePos)
																.setBlockAlign('left')
																.run()
														}
													>
														<AlignHorizontalJustifyStart
															width={16}
															className="shrink-0"
														/>
													</TabsTrigger>
													<TabsTrigger
														value="center"
														className="flex items-center gap-2"
														onClick={() =>
															editor
																.chain()
																.setNodeSelection(data.currentNodePos)
																.setBlockAlign('center')
																.run()
														}
													>
														<AlignHorizontalJustifyCenter
															width={16}
															className="shrink-0"
														/>
													</TabsTrigger>
													<TabsTrigger
														value="right"
														className="flex items-center gap-2"
														onClick={() =>
															editor
																.chain()
																.setNodeSelection(data.currentNodePos)
																.setBlockAlign('right')
																.run()
														}
													>
														<AlignHorizontalJustifyEnd
															width={16}
															className="shrink-0"
														/>
													</TabsTrigger>
												</TabsList>
											</Tabs>
										</div>
									)}
									{data.currentNode?.content.firstChild?.type.name ===
										'shortAnswer' && (
										<ShortAnswerSettings
											node={data.currentNode?.content.firstChild}
											pos={data.currentNodePos}
											editor={editor}
										/>
									)}
									{data.currentNode?.content.firstChild?.type.name ===
										'linkInput' && (
										<LinkInputSettings
											node={data.currentNode?.content.firstChild}
											pos={data.currentNodePos}
											editor={editor}
										/>
									)}
									{data.currentNode?.content.firstChild?.type.name ===
										'numberInput' && (
										<NumberInputSettings
											node={data.currentNode?.content.firstChild}
											pos={data.currentNodePos}
											editor={editor}
										/>
									)}
									{data.currentNode?.content.firstChild?.type.name ===
										'longAnswer' && (
										<LongAnswerSettings
											node={data.currentNode?.content.firstChild}
											pos={data.currentNodePos}
											editor={editor}
										/>
									)}
									{data.currentNode?.content.firstChild?.type.name ===
										'submitButton' && (
										<SubmitButtonSettings
											node={data.currentNode?.content.firstChild}
											pos={data.currentNodePos}
											editor={editor}
										/>
									)}
									{data.currentNode?.content.firstChild?.type.name ===
										'emailInput' && (
										<EmailInputSettings
											node={data.currentNode?.content.firstChild}
											pos={data.currentNodePos}
											editor={editor}
										/>
									)}
									{data.currentNode?.content.firstChild?.type.name ===
										'cryptoWalletInput' && (
										<CryptoWalletSettings
											node={data.currentNode?.content.firstChild}
											pos={data.currentNodePos}
											editor={editor}
										/>
									)}
									{data.currentNode?.content.firstChild?.type.name ===
										'dropdownInput' && (
										<DropdownSettings
											node={data.currentNode?.content.firstChild}
											pos={data.currentNodePos}
											editor={editor}
										/>
									)}
									{data.currentNode?.content.firstChild?.type.name ===
										'checkboxesInput' && (
										<CheckboxesSettings
											node={data.currentNode?.content.firstChild}
											pos={data.currentNodePos}
											editor={editor}
										/>
									)}
									{data.currentNode?.content.firstChild?.type.name ===
										'simple-form' && (
										<SimpleFormSettings
											node={data.currentNode?.content.firstChild}
											pos={data.currentNodePos}
											editor={editor}
										/>
									)}
								</Popover.Content>
							</Popover.Root>
							<div className="mx-1 h-6 w-[1px] shrink-0 bg-border"></div>
							<Button variant="ghost" size="s" className="w-8">
								<Grip width={16} className="shrink-0" />
							</Button>
						</div>
					</div>
				</DragHandle>
			)}
		</>
	)
}
